import { useCallback, useEffect, useMemo, useState } from "react";
import { ResetFiltersButton } from "@/components/common/ResetFiltersButton";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, X } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EntryModal } from "@/components/common/EntryModal";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { LaunchSummary } from "@/components/launch/LaunchSummary";
import { LaunchOverview } from "@/components/launch/LaunchOverview";
import { MilestoneTable } from "@/components/launch/MilestoneTable";
import { MilestoneKanban } from "@/components/launch/MilestoneKanban";
import { MilestoneTimeline } from "@/components/launch/MilestoneTimeline";
import { MilestoneDetail } from "@/components/launch/MilestoneDetail";
import { milestoneFields } from "@/components/launch/milestoneFields";
import { api } from "@/lib/api";
import { useUser } from "@/context/UserContext";
import { LAUNCH_OWNERS, LAUNCH_STATUS, LAUNCH_PRIORITY, LAUNCH_CATEGORIES, LAUNCH_WORKSTREAMS, isOverdue, launchStatusFromSlug, launchStatusToSlug } from "@/data/launchOptions";

const today = () => new Date().toISOString().slice(0, 10);
const PRANK = { Critical: 4, High: 3, Medium: 2, Low: 1 };
const SRANK = LAUNCH_STATUS.reduce((a, s, i) => ({ ...a, [s.value]: i }), {});

export default function LaunchTracker() {
  const { canWrite } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [milestones, setMilestones] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [readiness, setReadiness] = useState(0);
  const [workstreamReadiness, setWorkstreamReadiness] = useState({});
  const [bump, setBump] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const [q, setQ] = useState("");
  const [catF, setCatF] = useState("all");
  const [ownerF, setOwnerF] = useState("all");
  const [prioF, setPrioF] = useState("all");
  const [sort, setSort] = useState("due");
  const [showArchived, setShowArchived] = useState(false);

  // URL-driven view + status/special filter (query params = single source of truth)
  const viewParam = searchParams.get("view");
  const view = ["overview", "table", "kanban", "timeline"].includes(viewParam) ? viewParam : "overview";
  const statusF = launchStatusFromSlug(searchParams.get("status")) || "all";
  const specialFilter = ["overdue", "overdue-critical"].includes(searchParams.get("filter")) ? searchParams.get("filter") : null;
  const wsF = LAUNCH_WORKSTREAMS.some((w) => w.value === searchParams.get("workstream")) ? searchParams.get("workstream") : "all";

  const changeView = (v) => { const p = new URLSearchParams(searchParams); p.set("view", v); setSearchParams(p); };
  const setWsParam = (v) => { const p = new URLSearchParams(searchParams); if (v === "all") p.delete("workstream"); else p.set("workstream", v); setSearchParams(p); };
  const setStatusParam = (v) => { const p = new URLSearchParams(searchParams); if (v === "all") p.delete("status"); else { p.set("status", launchStatusToSlug(v)); p.delete("filter"); } setSearchParams(p); };
  const clearFilters = () => { const p = new URLSearchParams(searchParams); p.delete("status"); p.delete("filter"); setSearchParams(p); };

  const load = useCallback(async () => {
    try {
      const [m, s, all] = await Promise.all([api.list("launch-milestones"), api.list("launch-summary"), api.list("updates")]);
      setMilestones(m); setReadiness(s.readiness); setWorkstreamReadiness(s.workstream_readiness || {});
      setUpdates(all.filter((u) => u.area === "Launch"));
    } catch (err) { console.error("Failed to load launch data:", err); }
    finally { setLoaded(true); }
  }, []);
  useEffect(() => { load(); }, [load, bump]);

  // Direct milestone deep link: /launch?milestone=<id>
  useEffect(() => {
    const mid = searchParams.get("milestone");
    if (!mid || !loaded) return;
    const found = milestones.find((x) => x.id === mid);
    if (found) setDetail(found);
    else { toast.error("That milestone is unavailable — it may have been deleted."); const p = new URLSearchParams(searchParams); p.delete("milestone"); setSearchParams(p, { replace: true }); }
  }, [searchParams, loaded, milestones, setSearchParams]);

  const refresh = () => setBump((b) => b + 1);
  const byId = useMemo(() => Object.fromEntries(milestones.map((m) => [m.id, m])), [milestones]);
  const depsFor = (m) => (m.dependencies && m.dependencies.length ? m.dependencies : (m.dependency_id ? [m.dependency_id] : []));
  const depIncompleteFor = (m) => depsFor(m).some((id) => byId[id] && byId[id].status !== "Complete");

  const openAdd = () => { setEditing({ owner: "Steven", status: "Not started", priority: "Medium", category: "Other", completion: 0 }); setModalOpen(true); };
  const openEdit = (m) => { setDetail(null); setEditing(m); setModalOpen(true); };

  const save = async (v) => {
    if (v.dependency_id === "none") v.dependency_id = null;
    if (v.id && v.dependency_id === v.id) { toast.error("A milestone cannot depend on itself."); throw new Error("self-dep"); }
    if (v.id) { await api.update("launch-milestones", v.id, v); toast.success("Milestone updated"); }
    else { await api.create("launch-milestones", v); toast.success("Milestone added"); }
    refresh();
  };
  const complete = async (m) => { await api.update("launch-milestones", m.id, { status: "Complete", completion: 100, completed_date: today() }); toast.success("Milestone complete"); setDetail(null); refresh(); };
  const reopen = async (m) => { await api.update("launch-milestones", m.id, { status: "In progress", completed_date: "" }); toast.success("Milestone reopened"); setDetail(null); refresh(); };
  const archive = async (m) => { await api.update("launch-milestones", m.id, { archived: true }); toast.success("Milestone archived"); setDetail(null); refresh(); };
  const restore = async (m) => { await api.update("launch-milestones", m.id, { archived: false }); toast.success("Milestone restored"); setDetail(null); refresh(); };
  const changeStatus = async (id, status) => { await api.update("launch-milestones", id, status === "Complete" ? { status, completion: 100, completed_date: today() } : { status }); toast.success("Status updated"); refresh(); };
  const doDelete = async (reason) => { await api.remove("launch-milestones", confirm.id, reason); toast.success("Moved to Recycle Bin"); setConfirm(null); setDetail(null); refresh(); };

  const filtered = useMemo(() => {
    let rows = milestones.filter((m) => (showArchived ? !!m.archived : !m.archived));
    if (q) { const s = q.toLowerCase(); rows = rows.filter((m) => m.title.toLowerCase().includes(s)); }
    if (catF !== "all") rows = rows.filter((m) => m.category === catF);
    if (wsF !== "all") rows = rows.filter((m) => m.workstream === wsF);
    if (ownerF !== "all") rows = rows.filter((m) => m.owner === ownerF);
    if (statusF !== "all") rows = rows.filter((m) => m.status === statusF);
    if (prioF !== "all") rows = rows.filter((m) => m.priority === prioF);
    const tt = today();
    if (specialFilter === "overdue") rows = rows.filter((m) => isOverdue(m, tt));
    else if (specialFilter === "overdue-critical") rows = rows.filter((m) => isOverdue(m, tt) && m.priority === "Critical");
    const sorters = {
      due: (a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"),
      priority: (a, b) => (PRANK[b.priority] || 0) - (PRANK[a.priority] || 0),
      status: (a, b) => (SRANK[a.status] || 0) - (SRANK[b.status] || 0),
      completion: (a, b) => (b.completion || 0) - (a.completion || 0),
      title: (a, b) => a.title.localeCompare(b.title),
    };
    return [...rows].sort(sorters[sort]);
  }, [milestones, q, catF, wsF, ownerF, statusF, prioF, sort, showArchived, specialFilter]);

  const depOptions = useMemo(() => [{ value: "none", label: "No dependency" }, ...milestones.filter((m) => m.id !== editing?.id).map((m) => ({ value: m.id, label: m.title }))], [milestones, editing]);
  const t = today();
  const activeFilterLabel = specialFilter === "overdue" ? "overdue milestones" : specialFilter === "overdue-critical" ? "critical overdue milestones" : statusF !== "all" ? `${statusF} milestones` : null;

  const closeDetail = () => { setDetail(null); if (searchParams.get("milestone")) { const p = new URLSearchParams(searchParams); p.delete("milestone"); setSearchParams(p, { replace: true }); } };

  const Toolbar = (
    <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center">
      <div className="relative flex-1">
        <Search size={16} strokeWidth={2} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#5A6B8C]" />
        <Input data-testid="milestones-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search milestones…" className="rounded-full border-[#E4E9F3] bg-white pl-9 focus-visible:ring-[#E0A82E]/40" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <F testid="filter-workstream" v={wsF} on={setWsParam} label="Workstream" options={LAUNCH_WORKSTREAMS} />
        <F testid="filter-category" v={catF} on={setCatF} label="Category" options={LAUNCH_CATEGORIES} />
        <F testid="filter-owner" v={ownerF} on={setOwnerF} label="Owner" options={LAUNCH_OWNERS} />
        <F testid="filter-status" v={statusF} on={setStatusParam} label="Status" options={LAUNCH_STATUS} />
        <F testid="filter-priority" v={prioF} on={setPrioF} label="Priority" options={LAUNCH_PRIORITY} />
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="milestones-sort" className="w-auto min-w-[130px] rounded-full border-[#E4E9F3] bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="due">Sort: Due date</SelectItem>
            <SelectItem value="priority">Sort: Priority</SelectItem>
            <SelectItem value="status">Sort: Status</SelectItem>
            <SelectItem value="completion">Sort: Completion</SelectItem>
            <SelectItem value="title">Sort: Title</SelectItem>
          </SelectContent>
        </Select>
        <label className="ml-1 flex cursor-pointer items-center gap-2 text-xs font-semibold text-[#5A6B8C]">
          <Switch data-testid="milestones-show-archived" checked={showArchived} onCheckedChange={setShowArchived} />
          Show archived
        </label>
      </div>
    </div>
  );

  return (
    <>
      <PageHeader
        eyebrow="Launch Tracker"
        title="Launch Tracker"
        description="Everything required to launch Edju Cat — milestones, deadlines, ownership, dependencies, blockers and readiness."
        actions={canWrite ? <Button data-testid="add-milestone-btn" onClick={openAdd} className="rounded-full bg-[#E0A82E] text-white hover:bg-[#C79422]"><Plus size={16} /> Add Milestone</Button> : null}
      />

      <div className="mb-8"><LaunchSummary refreshKey={bump} onSettingsChange={refresh} /></div>

      {activeFilterLabel && (
        <div data-testid="launch-filter-banner" className="mb-4 flex items-center justify-between rounded-xl bg-[#E0A82E]/12 px-3 py-2 text-xs font-semibold text-[#A16207]">
          <span data-testid="launch-filter-label">Filtered to {activeFilterLabel} · {filtered.length} matching</span>
          <button data-testid="clear-launch-filter" onClick={clearFilters} className="inline-flex items-center gap-1 underline hover:no-underline"><X size={13} /> Clear filter</button>
        </div>
      )}

      <ResetFiltersButton /><Tabs value={view} onValueChange={changeView} className="w-full">
        <TabsList data-testid="launch-tabs" className="mb-6 flex h-auto flex-wrap justify-start gap-1.5 rounded-full border border-[#E4E9F3] bg-white p-1.5 shadow-sm">
          {["overview", "table", "kanban", "timeline"].map((v) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-full px-4 py-1.5 text-sm font-semibold capitalize text-[#5A6B8C] transition-colors data-[state=active]:text-white data-[state=active]:[background-color:var(--accent-primary)]">{v}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview"><LaunchOverview milestones={filtered} updates={updates} readiness={readiness} workstreamReadiness={workstreamReadiness} today={t} onOpen={setDetail} refreshKey={bump} /></TabsContent>
        <TabsContent value="table">{Toolbar}<MilestoneTable milestones={filtered} today={t} onOpen={setDetail} onAdd={canWrite ? openAdd : undefined} /></TabsContent>
        <TabsContent value="kanban">{Toolbar}<MilestoneKanban milestones={filtered} today={t} depIncompleteFor={depIncompleteFor} onOpen={setDetail} onStatusChange={canWrite ? changeStatus : undefined} /></TabsContent>
        <TabsContent value="timeline">{Toolbar}<MilestoneTimeline milestones={filtered} onOpen={setDetail} /></TabsContent>
      </Tabs>

      <EntryModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={editing?.id ? "Edit Milestone" : "Add Launch Milestone"}
        submitLabel={editing?.id ? "Save Changes" : "Add Milestone"}
        fields={milestoneFields(depOptions)}
        initialValues={editing}
        onSubmit={save}
      />
      <MilestoneDetail
        milestone={detail}
        dependencies={detail ? depsFor(detail).map((id) => byId[id]).filter(Boolean) : []}
        today={t}
        onOpenChange={(o) => !o && closeDetail()}
        onOpenMilestone={(dep) => dep && setDetail(dep)}
        canWrite={canWrite}
        onEdit={openEdit}
        onComplete={complete}
        onReopen={reopen}
        onArchive={archive}
        onRestore={restore}
        onDelete={(m) => setConfirm(m)}
        onUpdatesChange={refresh}
      />
      <ConfirmDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)} title="Delete this milestone?" onConfirm={doDelete} softDelete />
    </>
  );
}

const F = ({ testid, v, on, label, options }) => (
  <Select value={v} onValueChange={on}>
    <SelectTrigger data-testid={testid} className="w-auto min-w-[120px] rounded-full border-[#E4E9F3] bg-white"><SelectValue placeholder={label} /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{label}: All</SelectItem>
      {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);
